import React, { useState } from "react";
import { ErrorType } from "../../../types/common-type";
import handleClearError from "../../../core/methods/handleClearError";
import MultiSearchableSelect from "../../../core/common/MultiSearchableSelect";
import { useOrganizations_Verifier } from "../../../core/customhook/hooks/useOrganizations";
import { useProjectsByOrgId_Verifier } from "../../../core/customhook/hooks/projectHooks";
import { useLocationByProjectId_Verifier } from "../../../core/customhook/hooks/locationHooks";
import { usePrograms_Verifier } from "../../../core/customhook/hooks/programHooks";
import { useDepartmentByProgramId_Verifier } from "../../../core/customhook/hooks/departmentHooks";
export type SearchFormDataType = {
  case_id: string;
  applicant_name: string;
  email: string;
  from_date: string;
  to_date: string;
};

interface Option {
  value: string;
  label: string;
}

export interface FiltersSelectType {
  organization: string[];
  project: string[];
  location: string[];
  program: string[];
  departments: string[];
}

export type SearchPayloadType = FiltersSelectType & SearchFormDataType;

interface FilterProps {
  filterData: FiltersSelectType;

  setFilterData: React.Dispatch<React.SetStateAction<FiltersSelectType>>;
  searchFilter?: SearchFormDataType;
  setSearchFilter?: React.Dispatch<React.SetStateAction<SearchFormDataType>>;
  onSearch?: () => void;
}

export const initialFilterSelect: FiltersSelectType = {
  organization: [],
  project: [],
  location: [],
  program: [],
  departments: [],
};

const FiltersTrackCase: React.FC<FilterProps> = ({
  filterData,
  setFilterData,
  onSearch,
}) => {
  const [errors, setErrors] = useState<ErrorType>({});
  const {
    options: organizationOption,
    loading: loadingOrg,
    fetchData: fetchOrganizations,
  } = useOrganizations_Verifier();

  const {
    options: projectOptions,
    loading: loadingProject,
    fetchData: fetchProJect,
  } = useProjectsByOrgId_Verifier(filterData.organization.join(","));

  const {
    options: locationOptions,
    loading: loadingLocation,
    fetchData: fetchLocation,
  } = useLocationByProjectId_Verifier(filterData.project.join(","));

  const {
    options: programOptions,
    loading: loadingProgram,
    fetchData: fetchPrograms,
  } = usePrograms_Verifier(
    filterData.project.join(","),
    filterData.location.join(","),
  );

  const {
    options: departmentOptions,
    loading: loadingDepartment,
    fetchData: fetchDepartment,
  } = useDepartmentByProgramId_Verifier(filterData.program.join(","));

  const validateFilters = (): boolean => {
    const newErrors: ErrorType = {};
    if (!filterData.organization.length) {
      newErrors.organization = "Organization is required";
    }
    if (!filterData.project.length) {
      newErrors.project = "Account is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSelectDataChange = (
    name: string,
    values: string[],
    options: Option[],
  ) => {
    setFilterData((prev: FiltersSelectType) => {
      const updated: FiltersSelectType = {
        ...prev,
        [name]: values,
      };
      switch (name) {
        case "organization":
          updated.project = [];
          updated.location = [];
          updated.program = [];
          updated.departments = [];
          break;

        case "project":
          updated.location = [];
          updated.program = [];
          updated.departments = [];
          break;

        case "location":
          updated.program = [];
          updated.departments = [];
          break;

        case "program":
          updated.departments = [];
          break;

        default:
          break;
      }

      return updated;
    });
    handleClearError(setErrors, name);
  };
  const handleSelectSearch = () => {
    const isValid = validateFilters();
    if (!isValid) return;
    onSearch?.();
  };

  const handleClearAll = () => {
    setFilterData({
      organization: [],
      project: [],
      location: [],
      program: [],
      departments: [],
    });
    setErrors({});
  };
  return (
    <div className="py-3">
      <div className="row mt-4 mt-lg-3 mb-0 gx-2 gy-0">
        <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3">
          <MultiSearchableSelect
            label="Organization"
            name="organization"
            value={filterData.organization}
            options={organizationOption}
            onChange={handleSelectDataChange}
            error={errors.organization}
            loading={loadingOrg}
            onOpen={fetchOrganizations}
            required
          />
        </div>

        {/* Account */}
        <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3">
          <MultiSearchableSelect
            label="Account"
            name="project"
            value={filterData.project}
            options={projectOptions}
            onChange={handleSelectDataChange}
            error={errors.project}
            loading={loadingProject}
            onOpen={fetchProJect}
            disabled={!filterData.organization.length}
            required
          />
        </div>

        {/* Location */}
        <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3">
          <MultiSearchableSelect
            label="Location"
            name="location"
            value={filterData.location}
            options={locationOptions}
            onChange={handleSelectDataChange}
            error={errors.location}
            loading={loadingLocation}
            onOpen={fetchLocation}
            disabled={!filterData.project.length}
          />
        </div>

        {/* Program */}
        <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3">
          <MultiSearchableSelect
            label="Program"
            name="program"
            value={filterData.program}
            options={programOptions}
            onChange={handleSelectDataChange}
            error={errors.program}
            loading={loadingProgram}
            onOpen={fetchPrograms}
            disabled={!filterData.location.length}
          />
        </div>

        {/* Department */}
        <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3">
          <MultiSearchableSelect
            label="Department"
            name="departments"
            value={filterData.departments}
            options={departmentOptions}
            onChange={handleSelectDataChange}
            error={errors.departments}
            loading={loadingDepartment}
            onOpen={fetchDepartment}
            disabled={!filterData.program.length}
          />
        </div>

        {/* Buttons */}
        <div
          className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3 d-flex gap-2 align-items-end"
          style={{ height: "70px" }}
        >
          <button
            type="button"
            className="btn btn-primary danger w-50 py-2"
            onClick={handleSelectSearch}
          >
            Search
          </button>

          <button
            type="button"
            className="btn btn-secondary w-50 py-2"
            style={{ minWidth: "max-content" }}
            onClick={handleClearAll}
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FiltersTrackCase; 